import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import {
  MsalProvider,
  useIsAuthenticated,
  useMsal,
} from "@azure/msal-react";

import { msalInstance } from "../utils/auth/Auth-utils";
import { LOCAL_STORAGE, loginRequestScope } from "../utils/constantes";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro del AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children, msalConfig }) => {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthContextWrapper msalConfig={msalConfig}>{children}</AuthContextWrapper>
    </MsalProvider>
  )
};

const AuthContextWrapper = ({ children, msalConfig }) => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();

  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      await instance.initialize();
      if (isMounted) setLoading(false);
      
    };

    init();
    
    return () => {
        isMounted = false;
    };
  }, [instance]);


  const login = useCallback((scopes) => instance.loginRedirect({ scopes, redirectStartPage: process.env.REACT_APP_REDIRECT_URI }), [instance]);

  const logout = useCallback(async () => {
    if (msalConfig.cache?.cacheLocation === LOCAL_STORAGE) {
      localStorage.clear();
    } else {
      sessionStorage.clear();
    }
    await instance.logoutRedirect();
  }, [instance, msalConfig]);

  const account = accounts[0];

  const getTokenRenew = useCallback(async () => {
    try {
      const result = await instance.acquireTokenSilent({
        ...{ scopes: loginRequestScope },
        account: accounts[0],
      });

      return result.accessToken;
    } catch (err) {
      instance.acquireTokenRedirect({ scopes: loginRequestScope });
      return null;
    }
  }, [instance, accounts]);


  const contextValue = useMemo(
    () => ({ isAuthenticated, isLoading, account, login, logout, getTokenRenew }),
    [isAuthenticated, isLoading, account, login, logout, getTokenRenew]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
